import { decodeSecurityLevel } from '@/utils/approval/securityLevel';
import type {
  ApprovalAttributes,
  ApprovalDetailAttachment,
  ApprovalDetailResponse,
  Attachment,
} from '@/types/approval';

/**
 * 표시 파일명 — 원본명은 FILE_DESC(fileDescription)에 저장됨.
 * 비어 있는 과거/레거시 이관 문서는 fileName(스토리지 키) 폴백.
 */
const displayFileName = (a: ApprovalDetailAttachment): string =>
  a.fileDescription?.trim() || a.fileName;

const getExt = (name: string): string => {
  const idx = name.lastIndexOf('.');
  return idx < 0 ? '' : name.slice(idx + 1).toLowerCase();
};

/**
 * 확장자 추출. fileFormat 이 있으면 우선, 없으면 표시명 → fileName 순으로 파싱.
 */
const extractFormat = (a: ApprovalDetailAttachment): string =>
  a.fileFormat || getExt(displayFileName(a)) || getExt(a.fileName);

/**
 * GET 응답의 attachments[] → 폼 Attachment[].
 * - fileId 그대로 보관 (PUT 시 KeptAttachment 로 재전송)
 * - fileUuid 는 undefined — 신규 업로드 항목과 구별되는 마커
 * - 다운로드/이미지 URL 은 GET 응답의 `url` 키를 폼 내부 urlOrg 로 보관
 *   (AttachmentField 가 useDownloadUrl skip 하고 직접 사용. 이미지 썸네일도 동일 url).
 */
const toAttachment = (a: ApprovalDetailAttachment): Attachment => ({
  fileId: a.fileId,
  fileName: displayFileName(a),
  fileSize: a.fileSize,
  fileFormat: extractFormat(a),
  mimeType: a.mimeType,
  urlOrg: a.url ?? a.urlOrg ?? '',
  urlThOrg: a.urlThOrg,
  uploadDate: a.uploadDate ?? '',
});

/**
 * 편집 모드 진입 시 GET 응답 → ApprovalCreate 폼 상위 필드 + 기존 첨부.
 */
const approvalDetailToFormState = (detail: ApprovalDetailResponse) => ({
  docId: detail.documentId,
  subject: detail.subject ?? '',
  content: detail.content ?? '',
  ...decodeSecurityLevel(detail.security),
  showImagesBelowContent: detail.showImagesBelowContent ?? false,
  attributes: (detail.attributes ?? null) as ApprovalAttributes | null,
  attachments: (detail.attachments ?? []).map(toAttachment),
  status: detail.status, // S/P/R/C/H/D 편집 가능 여부 판정용
  approvers: detail.approvers ?? [], // type=1 + type=2 섞인 배열
  referrers: detail.referrers ?? [], // 참조자
  additionalReferrers: detail.additionalReferrers ?? [],
});

export type ApprovalDetailFormState = ReturnType<
  typeof approvalDetailToFormState
>;

export default approvalDetailToFormState;
